import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ─── Types ────────────────────────────────────────────────────────────────────
interface League {
  id: number; name: string; venue: string;
  city: string | null; country: string | null;
  start_date: string; end_date: string;
  organizer_name: string;
  hidden?: number;
}
interface Squad { id: number; name: string; date: string; }
interface BracketMatchupData {
  matchup_number: number;
  entry1_id: number | null; entry1_name: string | null; entry1_score: number | null;
  entry2_id: number | null; entry2_name: string | null; entry2_score: number | null;
  winner_entry_id: number | null; is_bye: boolean;
}
interface BracketRoundData {
  round_number: number; game_number: number | null; matchups: BracketMatchupData[];
}
interface BracketData {
  bracket_number: number; rounds: BracketRoundData[];
}
interface SEResult {
  id: number; name: string; type: string;
  applies_to: string; applies_to_game_numbers: string | null;
  hdcp?: boolean;
  standings?: any[];
  cashers?: any[];
  computed_rounds?: any[];
  mystery_target?: number | null;
  matching_bowlers?: any[];
  payouts?: { bowler_name: string; amount: number; notes: string | null }[];
  clean_game_winners?: string;
  pot_results?: any[];
  config?: any[];
  brackets?: BracketData[];
  totalRounds?: number;
  cost?: number;
}

// ─── Labels / Constants ───────────────────────────────────────────────────────
const TYPE_LABELS: Record<string, string> = {
  high_game: 'High Game', high_series: 'High Series',
  mystery_score: 'Mystery Score', mystery_series: 'Mystery Series',
  side_pots: 'Side Pots', survivor: 'Survivor',
  eliminator: 'Eliminator', clean_game: 'Clean Game',
  bracket: 'Bracket', custom: 'Custom',
};
const SKIP_PRINT_TYPES = new Set(['mystery_score', 'mystery_series', 'clean_game', 'bracket']);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const fmtDateShort = (d: string) =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// ─── Print helpers ────────────────────────────────────────────────────────────
function buildWinnersHTML(se: SEResult): string {
  if (se.type === 'survivor') {
    const rounds = se.computed_rounds || [];
    if (rounds.length === 0) return '<em style="color:#9ca3af;">No data yet</em>';
    const rows = rounds.map((r: any) => {
      const survived = (r.survivor_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      const elim = (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      return `<tr>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">G${r.game_number}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">Min: ${r.survivor_score}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;color:#15803d;">${survived}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim}</td>
      </tr>`;
    }).join('');
    const cashers = (se.cashers || []).map((c: any) => c.bowler_name).join(', ') || 'None yet';
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;">Game</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;">Threshold</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;color:#15803d;">Survived</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;color:#b91c1c;">Eliminated</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div style="margin-top:8px;padding:6px 10px;background:#fefce8;border:1px solid #ca8a04;border-radius:4px;font-size:12px;">
        <strong>Finalists:</strong> ${cashers}
      </div>`;
  }
  if (se.type === 'eliminator') {
    const rounds = se.computed_rounds || [];
    if (rounds.length === 0) return '<em style="color:#9ca3af;">No data yet</em>';
    const rows = rounds.map((r: any) => {
      const adv = (r.advancer_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      const elim = (r.eliminated_list || []).map((b: any) => `${b.bowler_name} (${b.score})`).join(', ') || '—';
      return `<tr>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">G${r.game_number}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;">Top ${r.advancement_percentage}%</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;color:#15803d;">${adv}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;color:#b91c1c;">${elim}</td>
      </tr>`;
    }).join('');
    const cashers = (se.cashers || []).map((c: any) => c.bowler_name).join(', ') || 'None yet';
    return `<table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead><tr>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;">Game</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;">Advance</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;color:#15803d;">Advancing</th>
        <th style="background:#f3f4f6;padding:4px 8px;text-align:left;color:#b91c1c;">Eliminated</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div style="margin-top:8px;padding:6px 10px;background:#fefce8;border:1px solid #ca8a04;border-radius:4px;font-size:12px;">
        <strong>Finalists:</strong> ${cashers}
      </div>`;
  }
  if (se.type === 'side_pots') {
    const pots = se.pot_results || [];
    if (pots.length === 0) return '<em style="color:#9ca3af;">No scores</em>';
    return pots.map((pot: any) => {
      const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
      const all: any[] = pot.all_entries || pot.winners || [];
      const rows = all.map((b: any, i: number) =>
        `<div style="padding:1px 0;${i < pot.winner_count ? 'color:#15803d;font-weight:600;' : ''}">${i + 1}. ${b.bowler_name} — ${b.score}${i < pot.winner_count ? ' ✓' : ''}</div>`
      ).join('');
      return `<div style="margin-bottom:8px;"><strong style="font-size:11px;color:#6b7280;">${label}</strong>
        <div style="margin-top:2px;font-size:12px;">${rows || '<em style="color:#9ca3af;">No scores</em>'}</div></div>`;
    }).join('');
  }
  const standings = se.standings || [];
  if (standings.length === 0) return '<em style="color:#9ca3af;">No scores yet</em>';
  return standings.map((s: any, i: number) => {
    const score = se.type === 'high_series' ? `${s.total_score} (series)` : `${s.best_game}`;
    return `<div style="padding:1px 0;">${i + 1}. ${s.bowler_name} — ${score}</div>`;
  }).join('');
}

function printSAResults(leagueName: string, scopeLabel: string, seResults: SEResult[]) {
  const printable = seResults.filter(se => !SKIP_PRINT_TYPES.has(se.type));
  if (printable.length === 0) { alert('No printable side event data for this scope.'); return; }
  const seSections = printable.map(se => {
    const typeLabel = TYPE_LABELS[se.type] || se.type;
    return `<div style="margin-bottom:20px;page-break-inside:avoid;">
      <h3 style="margin:0 0 4px;font-size:14px;">${se.name} <span style="color:#6b7280;font-weight:400;font-size:12px;">(${typeLabel})</span></h3>
      <div style="font-size:13px;line-height:1.7;">${buildWinnersHTML(se)}</div>
    </div>`;
  }).join('<hr style="border:none;border-top:1px solid #e5e7eb;margin:14px 0;"/>');
  const html = `<!DOCTYPE html><html><head><title>Results – ${leagueName}</title>
    <style>body{font-family:sans-serif;padding:24px;color:#111;}table{width:100%;border-collapse:collapse;}th{text-align:left;}</style>
    </head><body>
    <h2 style="margin-bottom:4px;">Results – ${leagueName}</h2>
    <p style="color:#6b7280;font-size:13px;margin-bottom:20px;">${scopeLabel}</p>
    ${seSections}
    <p style="color:#9ca3af;font-size:11px;margin-top:24px;">Printed ${new Date().toLocaleString()}</p>
    </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=700');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

function printBrackets(seName: string, brackets: any[], totalRounds: number, cost: number) {
  const buildMatchupHtml = (m: any | undefined): string => {
    if (!m) return '<table class="mu"><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr><tr><td class="mu-name"><em class="tbd">TBD</em></td><td class="mu-score"></td></tr></table>';
    const e1Won = m.winner_entry_id === m.entry1_id;
    const e2Won = m.winner_entry_id === m.entry2_id;
    const r1 = `<tr style="background:${e1Won ? '#d1fae5' : 'white'}"><td class="mu-name" style="font-weight:${e1Won ? '700' : 'normal'}">${m.entry1_name || '<em class="tbd">TBD</em>'}</td><td class="mu-score" style="font-weight:${e1Won ? '700' : 'normal'}">${m.entry1_score != null ? m.entry1_score : ''}</td></tr>`;
    const r2 = `<tr style="background:${e2Won ? '#d1fae5' : 'white'}"><td class="mu-name" style="font-weight:${e2Won ? '700' : 'normal'}">${m.is_bye ? '<em class="tbd">BYE</em>' : (m.entry2_name || '<em class="tbd">TBD</em>')}</td><td class="mu-score" style="font-weight:${e2Won ? '700' : 'normal'}">${!m.is_bye && m.entry2_score != null ? m.entry2_score : ''}</td></tr>`;
    return `<table class="mu"><tbody>${r1}${r2}</tbody></table>`;
  };
  const bracketHtml = brackets.map(b => {
    const cols = Array.from({ length: totalRounds }, (_, ri) => {
      const roundNum = ri + 1;
      const round = b.rounds.find((r: any) => r.round_number === roundNum);
      const slotCount = Math.pow(2, totalRounds - roundNum);
      const slotH = Math.pow(2, roundNum - 1) * 40;
      const header = roundNum === totalRounds ? 'Final' : `R${roundNum}${round ? ` G${round.game_number}` : ''}`;
      const cells = Array.from({ length: slotCount }, (_, mi) => {
        const matchup = round?.matchups?.[mi];
        return `<div class="slot" style="height:${slotH}px">${buildMatchupHtml(matchup)}</div>`;
      }).join('');
      return `<div class="col"><div class="col-hdr">${header}</div>${cells}</div>`;
    }).join('');
    return `<div class="bracket"><div class="b-title">Bracket #${b.bracket_number}</div><div class="b-rounds">${cols}</div></div>`;
  }).join('');
  const html = `<!DOCTYPE html><html><head><title>Brackets \u2013 ${seName}</title><style>
    @page{size:A4 portrait;margin:12mm}
    body{font-family:Arial,sans-serif;font-size:10px;color:#111;margin:0}
    h2{font-size:14px;margin:0 0 2px} .sub{color:#6b7280;font-size:10px;margin:0 0 10px}
    .page-columns{columns:2;column-gap:6mm}
    .bracket{break-inside:avoid;-webkit-column-break-inside:avoid;border:1px solid #d1d5db;border-radius:4px;padding:5px;margin-bottom:8px}
    .b-title{font-weight:700;font-size:10px;margin-bottom:4px}
    .b-rounds{display:flex;gap:4px}
    .col{flex:0 0 auto}
    .col-hdr{text-align:center;font-size:8px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:2px;margin-bottom:2px}
    .slot{display:flex;align-items:center}
    table.mu{width:82px;border-collapse:collapse;border:1px solid #9ca3af}
    table.mu td{border-bottom:1px solid #d1d5db;padding:1px 3px}
    table.mu tr:last-child td{border-bottom:none}
    .mu-name{max-width:62px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:7.5px}
    .mu-score{width:18px;text-align:right;font-variant-numeric:tabular-nums}
    .tbd{color:#9ca3af;font-size:8px}
    .footer{color:#9ca3af;font-size:9px;margin-top:10px}
  </style></head><body>
    <h2>Brackets \u2013 ${seName}</h2>
    <p class="sub">Entry: $${Number(cost).toFixed(2)} &bull; ${brackets.length} bracket${brackets.length !== 1 ? 's' : ''} &bull; ${totalRounds} rounds</p>
    <div class="page-columns">${bracketHtml}</div>
    <p class="footer">Printed ${new Date().toLocaleString()}</p>
  </body></html>`;
  const w = window.open('', '_blank', 'width=820,height=1060');
  if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 400); }
}

// ─── Bracket Tree Visual ──────────────────────────────────────────────────────
const PublicBracketTree: React.FC<{ bracket: BracketData; totalRounds: number }> = ({ bracket, totalRounds }) => (
  <div className="overflow-x-auto pb-2">
    <div className="flex gap-2" style={{ minWidth: `${totalRounds * 188}px` }}>
      {Array.from({ length: totalRounds }, (_, ri) => {
        const roundNum = ri + 1;
        const round = bracket.rounds.find(r => r.round_number === roundNum);
        const slotCount = Math.pow(2, totalRounds - roundNum);
        const slotHeightPx = Math.pow(2, roundNum - 1) * 58;
        const header = roundNum === totalRounds ? 'Final' : `Round ${roundNum}${round ? ` · G${round.game_number}` : ''}`;
        return (
          <div key={roundNum} className="flex flex-col" style={{ flex: '0 0 180px' }}>
            <div className="text-[10px] text-gray-500 text-center pb-1 border-b border-gray-700 mb-1">{header}</div>
            <div className="flex flex-col flex-1">
              {Array.from({ length: slotCount }, (_, mi) => {
                const m = round?.matchups?.[mi];
                const e1Won = m && m.winner_entry_id === m.entry1_id;
                const e2Won = m && m.winner_entry_id === m.entry2_id;
                return (
                  <div key={mi} className="flex items-center justify-center" style={{ height: `${slotHeightPx}px` }}>
                    <div className={`w-full border rounded overflow-hidden text-[11px] ${m ? 'border-gray-600' : 'border-gray-800 opacity-40'}`}>
                      <div className={`flex items-center justify-between px-2 py-1 border-b border-gray-700 ${e1Won ? 'bg-green-900/40 text-green-300 font-semibold' : m?.winner_entry_id ? 'text-gray-500' : 'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">{m?.entry1_name ?? <span className="text-gray-600 italic text-[10px]">TBD</span>}</span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {m?.entry1_score != null && <span className="font-mono text-[10px]">{m.entry1_score}</span>}
                          {e1Won && <span className="text-green-400 text-[10px]">✓</span>}
                        </div>
                      </div>
                      <div className={`flex items-center justify-between px-2 py-1 ${e2Won ? 'bg-green-900/40 text-green-300 font-semibold' : m?.winner_entry_id ? 'text-gray-500' : 'text-gray-300'}`}>
                        <span className="truncate max-w-[110px]">
                          {m?.is_bye ? <span className="text-gray-600 italic text-[10px]">BYE</span> : (m?.entry2_name ?? <span className="text-gray-600 italic text-[10px]">TBD</span>)}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                          {!m?.is_bye && m?.entry2_score != null && <span className="font-mono text-[10px]">{m.entry2_score}</span>}
                          {e2Won && <span className="text-green-400 text-[10px]">✓</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── SE Results Card ──────────────────────────────────────────────────────────
const SEResultCard: React.FC<{ se: SEResult }> = ({ se }) => {
  const typeLabel = TYPE_LABELS[se.type] || se.type;

  return (
    <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 overflow-hidden">
      {/* Card header */}
      <div className="px-4 py-3 bg-gray-700/40 flex items-center justify-between border-b border-gray-700/60">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white text-sm">{se.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-600/60 text-gray-300 border border-gray-500/40">{typeLabel}</span>
            {se.hdcp && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-600/20 text-teal-400 border border-teal-600/30">HDCP</span>
            )}
          </div>
          <button onClick={() => window.location.reload()} className="flex items-center gap-1 text-[9px] text-gray-500 hover:text-gray-300 transition-colors w-fit">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-2">
          {se.applies_to && se.applies_to !== 'individual' && (
            <span className="text-[10px] text-gray-400">{se.applies_to}</span>
          )}
          {se.type === 'bracket' && se.brackets && se.brackets.length > 0 && (
            <button
              onClick={() => printBrackets(se.name, se.brackets!, se.totalRounds ?? 3, se.cost ?? 0)}
              className="flex items-center gap-1 px-2 py-1 bg-indigo-700/50 hover:bg-indigo-700 text-white text-[10px] rounded-md font-medium transition-colors">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
              </svg>
              Print
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="px-4 py-3">

        {/* SURVIVOR */}
        {se.type === 'survivor' && (() => {
          const rounds = se.computed_rounds || [];
          if (rounds.length === 0)
            return <p className="text-xs text-gray-500 py-2">No rounds configured yet.</p>;
          return (
            <div className="space-y-2">
              {rounds.map((r: any) => (
                <div key={r.game_number} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-gray-400">Min Score: <strong className="text-white">{r.survivor_score}</strong></span>
                      {r.round_payout > 0 && <span className="text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs mb-2">
                    <span className="text-emerald-400">{r.survivors_count ?? 0} survived</span>
                    {(r.eliminated_count ?? 0) > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
                    {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
                  </div>
                  {r.survivor_list?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {r.survivor_list.map((b: any) => (
                        <span key={b.bowler_id} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300">
                          {b.bowler_name.split(' ')[0]} ({b.score})
                        </span>
                      ))}
                    </div>
                  )}
                  {r.eliminated_list?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.eliminated_list.map((b: any) => (
                        <span key={b.bowler_id} className="text-[10px] px-1.5 py-0.5 rounded line-through bg-red-900/30 text-red-400/80">
                          {b.bowler_name.split(' ')[0]} ({b.score})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {se.cashers && se.cashers.length > 0 && (
                <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <div className="text-xs font-bold text-yellow-300 mb-1.5">🏆 Finalists</div>
                  {se.cashers.map((c: any, i: number) => (
                    <div key={c.bowler_id} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="text-gray-400">{i + 1}.</span>
                      <span className="text-white">{c.bowler_name}</span>
                      {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ELIMINATOR */}
        {se.type === 'eliminator' && (() => {
          const rounds = se.computed_rounds || [];
          if (rounds.length === 0)
            return <p className="text-xs text-gray-500 py-2">No rounds configured yet.</p>;
          return (
            <div className="space-y-2">
              {rounds.map((r: any) => (
                <div key={r.game_number} className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/50">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-white">Game {r.game_number}</span>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-gray-400">Advance: <strong className="text-white">{r.advancement_percentage}%</strong></span>
                      {r.cutoff_score != null && <span className="text-gray-400">cutoff: {r.cutoff_score}</span>}
                      {r.round_payout > 0 && <span className="text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">${r.round_payout}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs mb-2">
                    <span className="text-orange-400">{r.advancers_count ?? 0} advancing</span>
                    {(r.eliminated_count ?? 0) > 0 && <span className="text-red-400">{r.eliminated_count} out</span>}
                    {r.pending_list?.length > 0 && <span className="text-gray-500">{r.pending_list.length} pending</span>}
                  </div>
                  {r.advancer_list?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {r.advancer_list.map((b: any) => (
                        <span key={b.bowler_id} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-900/40 text-orange-300">
                          {b.bowler_name.split(' ')[0]} ({b.score})
                        </span>
                      ))}
                    </div>
                  )}
                  {r.eliminated_list?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.eliminated_list.map((b: any) => (
                        <span key={b.bowler_id} className="text-[10px] px-1.5 py-0.5 rounded line-through bg-red-900/30 text-red-400/80">
                          {b.bowler_name.split(' ')[0]} ({b.score})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {se.cashers && se.cashers.length > 0 && (
                <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <div className="text-xs font-bold text-yellow-300 mb-1.5">🏆 Finalists</div>
                  {se.cashers.map((c: any, i: number) => (
                    <div key={c.bowler_id} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="text-gray-400">{i + 1}.</span>
                      <span className="text-white">{c.bowler_name}</span>
                      {c.payout > 0 && <span className="text-green-400 text-[10px]">+${c.payout}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* MYSTERY SCORE / MYSTERY SERIES */}
        {(se.type === 'mystery_score' || se.type === 'mystery_series') && (() => {
          const isSeries = se.type === 'mystery_series';
          const target = se.mystery_target ?? null;
          const matches: any[] = se.matching_bowlers || [];
          const payouts: { bowler_name: string; amount: number; notes: string | null }[] = se.payouts || [];
          return (
            <div>
              {target != null
                ? <p className="text-xs text-blue-400 mb-3">Target: <span className="font-bold text-white text-sm">{target}</span>{isSeries ? ' (series)' : ''}</p>
                : <p className="text-xs text-gray-500 mb-3">Target not set yet.</p>
              }
              {payouts.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-yellow-400 font-semibold mb-1.5">Paid Winners</p>
                  <div className="space-y-1.5">
                    {payouts.map((p, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 bg-yellow-900/30 border border-yellow-500/40">
                        <span className="text-sm font-medium text-white">{p.bowler_name}</span>
                        <span className="text-yellow-300 text-xs font-bold">${Number(p.amount).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {matches.length === 0
                ? <div className="text-xs text-gray-500 text-center py-3">{target != null ? 'No matches yet' : '—'}</div>
                : <div className="space-y-1.5">
                    <p className="text-xs text-green-400 font-semibold">{matches.length} match{matches.length > 1 ? 'es' : ''}!</p>
                    {matches.map((b: any) => (
                      <div key={b.bowler_id} className="flex items-center justify-between rounded-lg px-3 py-2 border bg-green-900/30 border-green-500/40">
                        <div>
                          <div className="text-sm font-medium text-white">{b.bowler_name}</div>
                          <div className="text-green-400 text-xs font-bold">
                            {isSeries ? `${b.total_score} series ✓` : (b.matching_games || []).map((g: any) => `G${g.game}: ${g.score}`).join(', ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
              }
            </div>
          );
        })()}

        {/* SIDE POTS */}
        {se.type === 'side_pots' && (() => {
          const pots = se.pot_results || [];
          if (pots.length === 0)
            return <p className="text-xs text-gray-500 py-2">No side pot results yet.</p>;
          return (
            <div className="space-y-3">
              {pots.map((pot: any, pi: number) => {
                const label = pot.config_type === 'game_series' ? `Series (G${pot.game_identifier})` : `Game ${pot.game_identifier}`;
                const all: any[] = pot.all_entries || pot.winners || [];
                const cutoff = all[pot.winner_count - 1]?.score ?? null;
                return (
                  <div key={pi} className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
                    <div className="px-3 py-2 bg-gray-700/50 border-b border-gray-600/50 flex items-center justify-between">
                      <span className="text-xs font-semibold text-white">{label}</span>
                      <span className="text-[10px] text-gray-400">Top {pot.winners_pct}% → {pot.winner_count} winner{pot.winner_count !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {all.length === 0
                        ? <div className="px-3 py-2 text-xs text-gray-500 text-center">No scores yet</div>
                        : all.map((b: any) => {
                            const isWinner = b.rank <= pot.winner_count;
                            const isTied = !isWinner && cutoff !== null && b.score === cutoff;
                            return (
                              <div key={b.bowler_id}
                                className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0
                                  ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                                <div className="flex items-center gap-1.5">
                                  <span>{b.rank}. {b.bowler_name}</span>
                                  {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full">TIE</span>}
                                </div>
                                <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>{b.score}</span>
                              </div>
                            );
                          })
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* HIGH GAME / HIGH SERIES */}
        {(se.type === 'high_game' || se.type === 'high_series') && (() => {
          const isHighSeries = se.type === 'high_series';
          const standings = se.standings || [];
          const winnerScore = standings.length > 0
            ? (isHighSeries ? standings[0].total_score : standings[0].best_game)
            : null;
          if (standings.length === 0)
            return <p className="text-xs text-gray-500 py-2">No scores entered yet.</p>;
          return (
            <div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
              {standings.map((s: any, i: number) => {
                const score = isHighSeries ? s.total_score : s.best_game;
                const isWinner = i === 0;
                const isTied = !isWinner && winnerScore !== null && score === winnerScore;
                return (
                  <div key={s.bowler_id}
                    className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0
                      ${isWinner ? 'bg-yellow-900/20 text-yellow-200' : isTied ? 'bg-orange-900/20 text-orange-200' : 'text-gray-400'}`}>
                    <div className="flex items-center gap-1.5">
                      <span>{i + 1}. {s.bowler_name}</span>
                      {isTied && <span className="text-[9px] bg-orange-700/60 text-orange-200 px-1.5 py-0.5 rounded-full">TIE</span>}
                    </div>
                    <span className={`font-bold ${isWinner ? 'text-yellow-300' : isTied ? 'text-orange-300' : ''}`}>
                      {score ?? '—'}{isHighSeries ? ' ser' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* BRACKET */}
        {se.type === 'bracket' && (() => {
          const brackets = se.brackets || [];
          const totalRounds = se.totalRounds ?? 3;
          if (brackets.length === 0)
            return <p className="text-xs text-gray-500 py-2 italic">No brackets set up yet.</p>;
          return (
            <div className="max-h-[520px] overflow-y-auto space-y-4 pr-1">
              {brackets.map((bracket) => (
                <div key={bracket.bracket_number} className="bg-gray-900/40 rounded-lg border border-gray-700/60 overflow-hidden">
                  <div className="px-3 py-1.5 bg-gray-700/40 border-b border-gray-700/60">
                    <span className="text-[11px] font-semibold text-indigo-300">Bracket #{bracket.bracket_number}</span>
                  </div>
                  <div className="p-2">
                    <PublicBracketTree bracket={bracket} totalRounds={totalRounds} />
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* CLEAN GAME */}
        {se.type === 'clean_game' && (
          <div className="text-xs text-gray-300 py-2">
            <span className="font-semibold text-white">Clean Game Winners: </span>
            {se.clean_game_winners || 'Not yet determined'}
          </div>
        )}

        {/* CUSTOM */}
        {se.type === 'custom' && (() => {
          const standings = se.standings || [];
          if (standings.length === 0)
            return <p className="text-xs text-gray-500 py-2">No scores yet.</p>;
          return (
            <div className="bg-gray-700/30 rounded-lg border border-gray-600/50 overflow-hidden">
              {standings.map((s: any, i: number) => (
                <div key={s.bowler_id}
                  className={`flex items-center justify-between px-3 py-1.5 text-xs border-b border-gray-700/50 last:border-0 ${i === 0 ? 'bg-yellow-900/20 text-yellow-200' : 'text-gray-400'}`}>
                  <span>{i + 1}. {s.bowler_name}</span>
                  <span className={`font-bold ${i === 0 ? 'text-yellow-300' : ''}`}>{s.best_game ?? s.total_score ?? '—'}</span>
                </div>
              ))}
            </div>
          );
        })()}

      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PublicSideActionView: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();

  const [league, setLeague] = useState<League | null>(null);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [selectedSquadId, setSelectedSquadId] = useState<number | null>(null);
  const [seResults, setSEResults] = useState<SEResult[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [loadingLeague, setLoadingLeague] = useState(true);
  const [leagueError, setLeagueError] = useState('');

  // Fetch league details + squads
  useEffect(() => {
    if (!leagueId) return;
    setLoadingLeague(true);
    axios.get(`${API_URL}/public/side-action-leagues/${leagueId}`)
      .then(r => {
        setLeague(r.data.league);
        setSquads(r.data.squads || []);
        if (r.data.squads?.length > 0) {
          setSelectedSquadId(r.data.squads[0].id);
        }
      })
      .catch(() => setLeagueError('Failed to load league details.'))
      .finally(() => setLoadingLeague(false));
  }, [leagueId]);

  // Fetch results whenever selected squad changes
  const fetchResults = useCallback((squadId: number) => {
    if (!leagueId) return;
    setLoadingResults(true);
    setResultsError('');
    axios.get(`${API_URL}/public/side-action-leagues/${leagueId}/squads/${squadId}/results`)
      .then(async (resultsRes) => {
        const results: SEResult[] = resultsRes.data || [];
        const hasBrackets = results.some(se => se.type === 'bracket');
        if (hasBrackets) {
          try {
            const bracketsRes = await axios.get(`${API_URL}/public/side-action-leagues/${leagueId}/squads/${squadId}/brackets`);
            const bracketSEs: any[] = bracketsRes.data?.sideEvents || [];
            const merged = results.map(se => {
              if (se.type === 'bracket') {
                const match = bracketSEs.find((b: any) => b.id === se.id);
                if (match) return { ...se, brackets: match.brackets, totalRounds: match.totalRounds, cost: match.cost };
              }
              return se;
            });
            setSEResults(merged);
          } catch {
            setSEResults(results);
          }
        } else {
          setSEResults(results);
        }
      })
      .catch(() => setResultsError('Failed to load results.'))
      .finally(() => setLoadingResults(false));
  }, [leagueId]);

  useEffect(() => {
    if (selectedSquadId != null) fetchResults(selectedSquadId);
  }, [selectedSquadId, fetchResults]);

  // Gather all-weeks results for printing
  const fetchAllResultsAndPrint = async () => {
    if (!leagueId || !league) return;
    const allResults: SEResult[] = [];
    for (const squad of squads) {
      try {
        const r = await axios.get(`${API_URL}/public/side-action-leagues/${leagueId}/squads/${squad.id}/results`);
        const results: SEResult[] = r.data || [];
        results.forEach(se => {
          const existing = allResults.find(x => x.id === se.id);
          if (!existing) allResults.push({ ...se, name: `${se.name} (${fmtDateShort(squad.date)})` });
        });
      } catch { /* skip failed squads */ }
    }
    printSAResults(league.name, 'All Weeks', allResults);
  };

  const selectedSquad = squads.find(s => s.id === selectedSquadId);

  if (loadingLeague) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading league…</p>
        </div>
      </div>
    );
  }

  if (leagueError || !league) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{leagueError || 'League not found.'}</p>
          <button onClick={() => navigate('/live/side-action')}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors">
            ← Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  if (league.hidden === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 bg-amber-900/40 border border-amber-600/40 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">League Under Maintenance</h2>
          <p className="text-gray-400 mb-6">We're making some improvements. Check back soon!</p>
          <button onClick={() => navigate('/live/side-action')}
            className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors">
            ← Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-lg border-b border-yellow-500/20 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/live/side-action')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700/60 hover:bg-gray-700 text-gray-300 hover:text-white text-sm rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Leagues
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-white font-bold text-lg leading-tight truncate">{league.name}</h1>
            <p className="text-yellow-400/80 text-xs truncate">{league.venue}{league.city ? ` · ${league.city}` : ''}</p>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-400">Organizer</div>
            <div className="text-sm text-yellow-300 font-medium">{league.organizer_name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* League info strip */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl border border-yellow-400/20 px-5 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-gray-400">Venue</div>
              <div className="text-white font-medium mt-0.5">{league.venue}</div>
            </div>
            {league.city && (
              <div>
                <div className="text-xs text-gray-400">Location</div>
                <div className="text-white font-medium mt-0.5">{league.city}{league.country ? `, ${league.country}` : ''}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-400">Start</div>
              <div className="text-white font-medium mt-0.5">{fmtDate(league.start_date)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">End</div>
              <div className="text-white font-medium mt-0.5">{fmtDate(league.end_date)}</div>
            </div>
          </div>
        </div>

        {/* Week tabs */}
        {squads.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {squads.map((sq, i) => (
              <button
                key={sq.id}
                onClick={() => setSelectedSquadId(sq.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all
                  ${selectedSquadId === sq.id
                    ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/30'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-gray-700/60'
                  }`}>
                Week {i + 1}
                <span className={`ml-1.5 text-xs ${selectedSquadId === sq.id ? 'text-yellow-100' : 'text-gray-500'}`}>
                  {fmtDateShort(sq.date)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Action buttons */}
        {selectedSquad && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 mr-auto">
              {selectedSquad.name || `Week ${squads.indexOf(selectedSquad) + 1}`} · {fmtDate(selectedSquad.date)}
            </span>
            <button
              onClick={() => printSAResults(league.name, `Week ${squads.indexOf(selectedSquad) + 1} – ${fmtDateShort(selectedSquad.date)}`, seResults)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700/60 hover:bg-blue-700 text-white text-xs rounded-lg font-medium transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
              </svg>
              Print This Week
            </button>
            <button
              onClick={fetchAllResultsAndPrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-900/60 hover:bg-blue-900 text-blue-200 text-xs rounded-lg font-medium transition-colors border border-blue-700/40">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
              </svg>
              Print All Weeks
            </button>
          </div>
        )}

        {/* SE Results grid */}
        {loadingResults ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400"></div>
          </div>
        ) : resultsError ? (
          <div className="text-center py-16 text-red-400 text-sm">{resultsError}</div>
        ) : seResults.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-sm">No side events found for this week.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {seResults.map(se => (
              <SEResultCard key={se.id} se={se} />
            ))}
          </div>
        )}

        {/* Public notice */}
        <div className="flex items-center gap-2 text-xs text-gray-600 pb-4">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Public view · Read-only · Powered by BowlerPro
        </div>
      </div>
    </div>
  );
};

export default PublicSideActionView;
